import React, { useEffect, useRef, useState } from 'react';
import { Layer, Stage, Image as KonvaImage, Line } from 'react-konva';

import Button from '../../atoms/Button';
import Konva from 'konva';
import IconButton from '../../atoms/IconButton';
import ButtonGroup from '../../atoms/ButtonGroup';
import Modal from '../../molecules/Modal';

type LineType = {
  points: number[];
  color: CanvasGradient | string;
  width: number;
};

type Point = {
  x: number;
  y: number;
};

interface FileImage {
  fileImage: HTMLImageElement;
  fileDimensions: FileDimensions;
}

interface FileDimensions {
  width: number;
  height: number;
}

export interface ImageFileEditProps {
  base64Image: string;
  index: number;
  closeViewer: () => void;
  changeFileData?: (i: number, name: string) => void;
}

interface ActionMode {
  messages: string[];
  pendingAction: () => void;
}

const defaultActionMode: ActionMode = {
  messages: ['', '', ''],
  pendingAction: Function.prototype as () => void,
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DRAW_LINE_COLOR = 'rgb(0,0,0)';
const DRAW_LINE_WIDTH: Record<'normal' | 'bold', number> = { normal: 12, bold: 24 };

const ON_SAVE_MESSAGES = [
  'マスクした画像を保存します。',
  '商談情報の保存を押下するまで、画像の編集は確定しません。',
  '保存すると編集した画像は元に戻せません。',
];
const ON_CLOSE_MESSAGES = ['画像編集モードを終了します', '編集中の画像は保存されていません。', '編集を中止しますか？'];
const ON_CLEAR_ALL_MESSAGES = ['編集前の状態に戻します', '編集した内容は元に戻せません', '本当に全ての線を削除しますか？'];

const ImageFileEdit = ({ base64Image, index, closeViewer, changeFileData }: ImageFileEditProps) => {
  // --- state ---
  const [dimensions, setDimensions] = useState<FileImage | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [actionMode, setActionMode] = useState<ActionMode>(defaultActionMode);
  const [drawLine, setDrawLine] = useState<LineType>();
  const [lines, setLines] = useState<LineType[]>([]);
  const [drawMode, setDrawMode] = useState<'normal' | 'bold'>('bold');
  const [stageScale, setStageScale] = useState<number>(1);
  const [stagePosition, setStagePosition] = useState<Point>({ x: 0, y: 0 });

  // --- refs ---
  const stageRef = useRef<Konva.Stage | null>(null);
  const scaleRef = useRef<number>(1);
  const positionRef = useRef<Point>({ x: 0, y: 0 });
  const lastCenterRef = useRef<Point | null>(null);
  const lastDistanceRef = useRef<number>(0);
  const isPinchingRef = useRef<boolean>(false);

  // 画像をキャンパスに表示する際の倍率
  // GINIEブラウザの場合は0.55 それ以外は1
  // GINIEブラウザ判定に"window.external.ShowPDFInfoByBase64"を使用しているが、
  // 実際にShowPDFInfoByBase64関数を実行するわけではないので、ミスリードに注意
  const zoomMagnification = window.external.ShowPDFInfoByBase64 ? 0.55 : 1;
  const zoomMagnificationOnSave = 1 / zoomMagnification;

  const isZoomed = stageScale > MIN_SCALE;

  // --- 画像読み込み ---

  useEffect(() => {
    const getImageDimensions = (base64: string): Promise<FileImage> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            fileImage: img,
            fileDimensions: { width: img.width, height: img.height },
          });
        };
        img.onerror = reject;
        img.src = base64;
      });
    };

    if (base64Image) {
      getImageDimensions(base64Image)
        .then(setDimensions)
        .catch(() => {
          console.log('画像データが破損しています');
        });
    }
  }, [base64Image]);

  // --- ユーティリティ ---

  const getTouchDistance = (pointA: Point, pointB: Point) => {
    return Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);
  };

  const getTouchCenter = (pointA: Point, pointB: Point): Point => ({
    x: (pointA.x + pointB.x) / 2,
    y: (pointA.y + pointB.y) / 2,
  });

  const getClampedPosition = (scaleValue: number, position: Point): Point => {
    /* istanbul ignore next */
    if (!dimensions?.fileDimensions) return position;

    const stageWidth = dimensions.fileDimensions.width * zoomMagnification;
    const stageHeight = dimensions.fileDimensions.height * zoomMagnification;
    const minX = stageWidth * (1 - scaleValue);
    const minY = stageHeight * (1 - scaleValue);

    return {
      x: Math.min(0, Math.max(minX, position.x)),
      y: Math.min(0, Math.max(minY, position.y)),
    };
  };

  const applyStageTransform = (nextScale: number, nextPosition: Point) => {
    const clampedPosition = getClampedPosition(nextScale, nextPosition);
    scaleRef.current = nextScale;
    positionRef.current = clampedPosition;
    setStageScale(nextScale);
    setStagePosition(clampedPosition);
  };

  const getStagePointerPosition = (stage: Konva.Stage): Point | null => {
    const pointerPosition = stage.getPointerPosition();
    if (!pointerPosition) return null;
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(pointerPosition);
  };

  // --- 確認ダイアログ ---

  const handleWithConfirm = (messages: string[], action: () => void) => {
    setActionMode({ messages, pendingAction: action });
    setShowDialog(true);
  };

  const handleConfirm = () => {
    const pendingAction = actionMode.pendingAction;
    setShowDialog(false);
    setActionMode(defaultActionMode);
    pendingAction();
  };

  const handleCancel = () => {
    setShowDialog(false);
    setActionMode(defaultActionMode);
  };

  // --- 保存 ---

  const handleSave = () => {
    const stage = stageRef.current;
    if (stage !== null) {
      const previousScale = { x: stage.scaleX(), y: stage.scaleY() };
      const previousPosition = { x: stage.x(), y: stage.y() };

      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.batchDraw();

      const dataUrl = stage.toDataURL({ mimeType: 'image/jpeg', pixelRatio: zoomMagnificationOnSave });

      stage.scale(previousScale);
      stage.position(previousPosition);
      stage.batchDraw();

      changeFileData && changeFileData(index, dataUrl.split(',')[1]);
    }
    closeViewer();
  };

  // --- 描画 ---

  const handleMouseDown = (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    event.evt.preventDefault();
    const stage = event.target.getStage();
    if (!stage) return;
    const position = getStagePointerPosition(stage);
    if (!position) return;
    setDrawLine({ points: [position.x, position.y], color: DRAW_LINE_COLOR, width: DRAW_LINE_WIDTH[drawMode] });
  };

  const handleMouseMove = (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (!drawLine?.points) return;
    event.evt.preventDefault();
    const stage = event.target.getStage();
    if (!stage) return;
    const position = getStagePointerPosition(stage);
    if (!position) return;
    setDrawLine({ points: [...drawLine.points, position.x, position.y], color: DRAW_LINE_COLOR, width: DRAW_LINE_WIDTH[drawMode] });
  };

  const handleMouseUp = () => {
    if (!drawLine?.points) return;
    const completed = drawLine;
    setDrawLine(undefined);
    setLines((prev) => [...prev, { points: completed.points, color: completed.color, width: completed.width }]);
  };

  // --- タッチ操作 ---

  const handleTouchStart = (event: Konva.KonvaEventObject<TouchEvent>) => {
    const stage = event.target.getStage();
    if (!stage) return;

    const touches = event.evt.touches;
    if (touches.length === 2) {
      event.evt.preventDefault();
      const rect = stage.container().getBoundingClientRect();
      const pointA = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top };
      const pointB = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top };
      isPinchingRef.current = true;
      setDrawLine(undefined);
      lastCenterRef.current = getTouchCenter(pointA, pointB);
      lastDistanceRef.current = getTouchDistance(pointA, pointB);
      return;
    }

    if (touches.length === 1 && !isPinchingRef.current) {
      handleMouseDown(event);
    }
  };

  const handleTouchMove = (event: Konva.KonvaEventObject<TouchEvent>) => {
    const stage = event.target.getStage();
    if (!stage) return;

    const touches = event.evt.touches;
    if (touches.length === 2) {
      event.evt.preventDefault();
      const rect = stage.container().getBoundingClientRect();
      const pointA = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top };
      const pointB = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top };
      const center = getTouchCenter(pointA, pointB);
      const distance = getTouchDistance(pointA, pointB);

      if (!lastCenterRef.current || lastDistanceRef.current === 0) {
        lastCenterRef.current = center;
        lastDistanceRef.current = distance;
        return;
      }

      const previousScale = scaleRef.current;
      const previousPosition = positionRef.current;
      const pointTo = {
        x: (lastCenterRef.current.x - previousPosition.x) / previousScale,
        y: (lastCenterRef.current.y - previousPosition.y) / previousScale,
      };
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, previousScale * (distance / lastDistanceRef.current)));
      applyStageTransform(nextScale, {
        x: center.x - pointTo.x * nextScale,
        y: center.y - pointTo.y * nextScale,
      });
      lastCenterRef.current = center;
      lastDistanceRef.current = distance;
      return;
    }

    if (touches.length === 1 && !isPinchingRef.current) {
      handleMouseMove(event);
    }
  };

  const handleTouchEnd = (event: Konva.KonvaEventObject<TouchEvent>) => {
    if (event.evt.touches.length < 2) {
      isPinchingRef.current = false;
      lastCenterRef.current = null;
      lastDistanceRef.current = 0;
    }
    handleMouseUp();
  };

  // --- ホイール・ズーム ---

  const handleWheel = (event: Konva.KonvaEventObject<WheelEvent>) => {
    const stage = event.target.getStage();
    if (!stage) return;
    event.evt.preventDefault();

    const pointerPosition = stage.getPointerPosition();
    if (!pointerPosition) return;

    const previousScale = scaleRef.current;
    const previousPosition = positionRef.current;

    if (event.evt.ctrlKey || event.evt.metaKey) {
      const scaleBy = 1.03;
      const zoomDirection = event.evt.deltaY > 0 ? -1 : 1;
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, previousScale * (zoomDirection > 0 ? scaleBy : 1 / scaleBy)));
      const pointTo = {
        x: (pointerPosition.x - previousPosition.x) / previousScale,
        y: (pointerPosition.y - previousPosition.y) / previousScale,
      };
      applyStageTransform(nextScale, {
        x: pointerPosition.x - pointTo.x * nextScale,
        y: pointerPosition.y - pointTo.y * nextScale,
      });
      return;
    }

    if (previousScale <= MIN_SCALE) return;

    applyStageTransform(previousScale, {
      x: previousPosition.x - event.evt.deltaX,
      y: previousPosition.y - event.evt.deltaY,
    });
  };

  const handleResetZoom = () => {
    applyStageTransform(MIN_SCALE, { x: 0, y: 0 });
    isPinchingRef.current = false;
    lastCenterRef.current = null;
    lastDistanceRef.current = 0;
  };

  return (
    <>
      <div className='camera-edit'>
        <div className='camera-edit__header'>
          <div className='camera-edit__header-left'>
            <IconButton icon='close' data-testid='btn-close-editor' onClick={() => handleWithConfirm(ON_CLOSE_MESSAGES, closeViewer)} />
          </div>
          <div className='camera-edit__header-center'>
            <ButtonGroup>
              <ButtonGroup.Button
                leftIcon='fiber_manual_record'
                iconClassName='u-icon-s'
                label='小'
                onClick={() => setDrawMode('normal')}
                isActive={drawMode === 'normal'}
              />
              <ButtonGroup.Button
                leftIcon='fiber_manual_record'
                iconClassName='u-icon-l'
                label='大'
                onClick={() => setDrawMode('bold')}
                isActive={drawMode === 'bold'}
              />
            </ButtonGroup>

            <Button
              leftIcon='undo'
              type='outlined'
              className='u-ml12'
              disabled={lines.length === 0}
              label='戻す'
              onClick={() => setLines((prev) => prev.filter((_, i) => i !== prev.length - 1))}
            />
            <Button
              leftIcon='cancel'
              type='outlined'
              isDanger={true}
              className='u-ml12'
              disabled={lines.length === 0}
              label='全て戻す'
              onClick={() => handleWithConfirm(ON_CLEAR_ALL_MESSAGES, () => setLines([]))}
            />
          </div>
          <div className='camera-edit__header-right'>
            {isZoomed ? (
              <Button leftIcon='zoom_out_map' type='outlined' className='u-mr12' label='元のサイズに戻す' onClick={handleResetZoom} />
            ) : (
              <Button label='保存' onClick={() => handleWithConfirm(ON_SAVE_MESSAGES, handleSave)} />
            )}
          </div>
        </div>

        {!!dimensions?.fileImage && dimensions.fileDimensions && (
          <div className='camera-edit__content camera-left-drawer__content'>
            <div
              style={{
                border: '1px solid #ccc',
                width: `${dimensions.fileDimensions.width * zoomMagnification}px`,
                height: `${dimensions.fileDimensions.height * zoomMagnification}px`,
                margin: 'auto',
                touchAction: 'none',
              }}>
              <Stage
                width={dimensions.fileDimensions.width * zoomMagnification}
                height={dimensions.fileDimensions.height * zoomMagnification}
                x={stagePosition.x}
                y={stagePosition.y}
                scaleX={stageScale}
                scaleY={stageScale}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ref={stageRef as any}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}>
                <Layer>
                  <KonvaImage
                    image={dimensions.fileImage}
                    x={0}
                    y={0}
                    width={dimensions.fileDimensions.width * zoomMagnification}
                    height={dimensions.fileDimensions.height * zoomMagnification}
                  />
                  {[...lines, drawLine].map(
                    (line, index) =>
                      line && (
                        <Line
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          points={line.points}
                          stroke={line.color}
                          strokeWidth={line.width}
                          tension={0.5}
                          lineCap='round'
                          lineJoin='round'
                        />
                      ),
                  )}
                </Layer>
              </Stage>
            </div>
          </div>
        )}
      </div>

      {showDialog && (
        <Modal open={showDialog} surfaceClassName='u-mv-auto'>
          <Modal.Header hasCloseButton={false}>{actionMode.messages[0]}</Modal.Header>
          <Modal.Content className='u-pb24'>
            <div>{actionMode.messages[1]}</div>
            <div>{actionMode.messages[2]}</div>
          </Modal.Content>
          <Modal.Actions>
            <Button type='secondary' label='キャンセル' onClick={handleCancel} />
            <Button label='OK' onClick={handleConfirm} data-testid='btn_save' />
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};

export default ImageFileEdit;
